/*
 * DSD Social Route Mutation Containment – p11dz116
 * Purpose:
 * - Keep p11dz108 diagnostics frozen.
 * - Reduce non-social Shopify/Dawn carousel mutation churn on DSD social routes.
 * - Stop hidden commerce/template bleed sliders from continuing Slick autoplay mutations
 *   after RouteShell suppresses them on /pages/members and /pages/user-profile.
 * - Keep the containment observer lifecycle bounded so the fix does not become
 *   another source of body/header mutation overhead.
 * Safety:
 * - Social routes only.
 * - Does not alter canonical DSD feed/profile/modal/composer systems.
 * - Does not touch diagnostics, Firebase writes, preloader, or overlay behavior.
 */
(function(){
  'use strict';
  var VERSION = 'p11dz116-social-route-containment-rollback-guard-v1';
  if (window.DSDSocialRouteMutationContainment && window.DSDSocialRouteMutationContainment.version === VERSION) return;

  function now(){ try { return new Date().toISOString(); } catch(e){ return ''; } }
  function isSocialRoute(){ return /\/pages\/(users|user-profile)/.test(location.pathname || ''); }
  function registry(){ return window.DSDRegistry || window.DSDRuntimeRegistry || null; }
  function claim(surface, owner, meta){
    try { var r = registry(); if (r && typeof r.claim === 'function') r.claim(surface, owner, meta || {}); } catch(e) {}
  }
  function event(code, detail){
    try {
      var r = registry();
      if (r && typeof r.event === 'function') r.event(code, detail || {});
      else if (r && typeof r.track === 'function') r.track(code, detail || {});
    } catch(e) {}
  }

  var state = {
    version: VERSION,
    route: location.pathname || '/',
    startedAt: now(),
    runs: 0,
    sectionsContained: 0,
    slickPaused: 0,
    slickUnslicked: 0,
    hiddenSlickContained: 0,
    visibleSocialSkipped: 0,
    flickityPaused: 0,
    lastRunAt: null,
    lastReason: '',
    active: false,
    observerReady: false,
    observerDisconnected: false,
    scheduled: false,
    skippedSchedules: 0,
    lastObservedAddedNodes: 0,
    rollbackRepairs: 0,
    nonBleedLooseSlickSkipped: 0,
    protectedSocialCandidates: 0
  };

  var SECTION_SELECTOR = [
    '[data-dsd-suppressed-social-bleed="true"]',
    '.shopify-section.section-collection-list',
    '[id*="collection_list"]',
    '[id*="collection-list"]',
    '[id*="featured_collection"]',
    '.shopify-section:has(.collection-list-wrapper)',
    '.shopify-section:has(.collection-list)',
    '.shopify-section:has(.featured-collection)'
  ].join(',');

  function qsa(root, selector){
    try { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); } catch(e) { return []; }
  }

  function isInsideCanonicalDSD(el){
    try { return !!(el && el.closest && el.closest('#dsd-feed-container,#dsd-profile-container,#dsd-canonical-social-autoshell,#dsd-canonical-profile-autoshell,.dsd-overlay-root')); } catch(e) { return false; }
  }

  function isBleedSection(el){
    if (!el || isInsideCanonicalDSD(el)) return false;
    if (el.getAttribute && el.getAttribute('data-dsd-suppressed-social-bleed') === 'true') return true;
    var id = el.id || '';
    var cls = (typeof el.className === 'string') ? el.className : '';
    if (/collection[_-]?list|featured[_-]?collection/.test(id)) return true;
    if (/section-collection-list|collection-list|featured-collection/.test(cls)) return true;
    try {
      if (el.querySelector && (el.querySelector('.collection-list-wrapper,.collection-list,.featured-collection') || el.querySelector('[id*="collection_list"],[id*="collection-list"]'))) return true;
    } catch(e) {}
    return false;
  }

  function isVisibleEnough(el){
    try {
      var r = el.getBoundingClientRect && el.getBoundingClientRect();
      var style = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0')) return false;
      return !!(r && r.width > 2 && r.height > 2);
    } catch(e) { return false; }
  }

  function isInsideVisibleSocialSurface(el){
    try {
      var social = el && el.closest && el.closest('#dsd-feed-container,#dsd-profile-container,.dsd-overlay-root,[data-dsd-contract="member-discovery"],[data-dsd-contract="profile-composer"]');
      return !!(social && isVisibleEnough(social));
    } catch(e) { return false; }
  }

  function hasProtectedDSDSurface(el){
    try {
      return !!(el && el.querySelector && el.querySelector('#dsd-feed-container,#dsd-profile-container,.dsd-overlay-root,[data-dsd-owner],[data-dsd-contract="member-discovery"],[data-dsd-contract="profile-composer"],.dsd-post-card,.dsd-discovery-card,.dsd-profile-card'));
    } catch(e) { return false; }
  }

  function closestBleedContext(el){
    try { return el && el.closest && el.closest('[data-dsd-suppressed-social-bleed="true"],[data-dsd-mutation-contained],.shopify-section.section-collection-list,[id*="collection_list"],[id*="collection-list"],[id*="featured_collection"]'); } catch(e) { return null; }
  }

  function rememberOriginal(el){
    if (!el || !el.setAttribute || el.getAttribute('data-dsd-containment-original-style') !== null) return;
    try { el.setAttribute('data-dsd-containment-original-style', el.getAttribute('style') || ''); } catch(e) {}
    try { el.setAttribute('data-dsd-containment-original-aria-hidden', el.getAttribute('aria-hidden') || ''); } catch(e2) {}
  }

  function restoreIfProtected(el, reason){
    if (!el || !el.getAttribute || !hasProtectedDSDSurface(el)) return false;
    try {
      var oldStyle = el.getAttribute('data-dsd-containment-original-style');
      if (oldStyle !== null) {
        if (oldStyle) el.setAttribute('style', oldStyle);
        else el.removeAttribute('style');
      }
      var oldAria = el.getAttribute('data-dsd-containment-original-aria-hidden');
      if (oldAria !== null) {
        if (oldAria) el.setAttribute('aria-hidden', oldAria);
        else el.removeAttribute('aria-hidden');
      }
      el.removeAttribute('data-dsd-mutation-contained');
      el.removeAttribute('data-dsd-suppressed-social-bleed');
      el.removeAttribute('data-dsd-suppressed-reason');
    } catch(e) {}
    state.rollbackRepairs++;
    claim('social-route-mutation-containment:protected-surface-restored', 'RuntimeStabilizer', {
      version: VERSION,
      route: state.route,
      reason: reason || 'protected-dsd-surface',
      repairs: state.rollbackRepairs
    });
    return true;
  }

  function containHiddenLooseSlick(){
    var contained = 0;
    qsa(document, '.slick-initialized,.slick-slider').forEach(function(el){
      if (!el || el.getAttribute('data-dsd-slick-contained') === VERSION) return;
      if (isInsideVisibleSocialSurface(el)) { state.visibleSocialSkipped++; return; }
      var bleedContext = closestBleedContext(el);
      var shouldContain = !!bleedContext;
      // p11dz116 rollback guard: do not globally unslick every hidden slider.
      // Only contain loose Slick when it is inside a proven bleed/commerce context.
      if (!shouldContain) { state.nonBleedLooseSlickSkipped++; return; }
      if (bleedContext && hasProtectedDSDSurface(bleedContext)) { state.protectedSocialCandidates++; return; }
      rememberOriginal(el);
      el.setAttribute('data-dsd-slick-contained', VERSION);
      try {
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
          var $el = window.jQuery(el);
          if ($el.hasClass('slick-initialized')) {
            try { $el.slick('slickPause'); } catch(e1) {}
            try { $el.slick('unslick'); state.slickUnslicked++; } catch(e2) {}
          }
        }
      } catch(e3) {}
      try { el.style.display = 'none'; el.style.visibility = 'hidden'; el.style.pointerEvents = 'none'; } catch(e4) {}
      contained++;
    });
    state.hiddenSlickContained += contained;
    return contained;
  }

  function containSection(section){
    if (!section || !isBleedSection(section)) return false;
    if (hasProtectedDSDSurface(section)) { state.protectedSocialCandidates++; restoreIfProtected(section, 'candidate-contained-dsd-surface'); return false; }
    if (section.getAttribute('data-dsd-mutation-contained') === VERSION) return false;
    rememberOriginal(section);
    section.setAttribute('data-dsd-mutation-contained', VERSION);
    section.setAttribute('data-dsd-suppressed-social-bleed','true');
    section.setAttribute('data-dsd-suppressed-reason', section.getAttribute('data-dsd-suppressed-reason') || 'commerce section isolated on canonical social route');
    section.setAttribute('aria-hidden','true');
    try { section.style.display = 'none'; section.style.visibility = 'hidden'; section.style.pointerEvents = 'none'; } catch(e) {}
    state.sectionsContained++;
    return true;
  }

  function containSlick(section){
    var count = 0;
    var unslicked = 0;
    var nodes = qsa(section, '.slick-initialized, .slick-slider, .slider2, .slider3, .slider4, .slider5, .slider6');
    nodes.forEach(function(el){
      if (!el || el.getAttribute('data-dsd-slick-contained') === VERSION) return;
      if (isInsideVisibleSocialSurface(el) || hasProtectedDSDSurface(el)) { state.visibleSocialSkipped++; return; }
      rememberOriginal(el);
      el.setAttribute('data-dsd-slick-contained', VERSION);
      try { el.setAttribute('aria-hidden','true'); } catch(e) {}
      try {
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
          var $el = window.jQuery(el);
          if ($el.hasClass('slick-initialized')) {
            try { $el.slick('slickPause'); count++; } catch(e1) {}
            try { $el.slick('unslick'); unslicked++; } catch(e2) {}
          }
        }
      } catch(e) {}
      try { el.style.display = 'none'; el.style.visibility = 'hidden'; el.style.pointerEvents = 'none'; } catch(e3) {}
    });
    state.slickPaused += count;
    state.slickUnslicked += unslicked;
    return { paused: count, unslicked: unslicked };
  }

  function containFlickity(section){
    var count = 0;
    qsa(section, '.flickity-enabled').forEach(function(el){
      if (!el || el.getAttribute('data-dsd-flickity-contained') === VERSION) return;
      el.setAttribute('data-dsd-flickity-contained', VERSION);
      try {
        if (window.Flickity) {
          var inst = window.Flickity.data(el);
          if (inst && typeof inst.pausePlayer === 'function') { inst.pausePlayer(); count++; }
        }
      } catch(e) {}
      try { el.style.display = 'none'; el.style.visibility = 'hidden'; el.style.pointerEvents = 'none'; } catch(e2) {}
    });
    state.flickityPaused += count;
    return count;
  }

  function run(reason){
    if (!isSocialRoute()) return state;
    state.active = true;
    state.runs++;
    state.lastRunAt = now();
    state.lastReason = reason || 'manual';
    var sections = qsa(document, SECTION_SELECTOR).filter(isBleedSection);
    var contained = 0;
    var slickPaused = 0;
    var slickUnslicked = 0;
    var flickityPaused = 0;
    sections.forEach(function(section){
      if (containSection(section)) contained++;
      var s = containSlick(section);
      slickPaused += s.paused;
      slickUnslicked += s.unslicked;
      flickityPaused += containFlickity(section);
    });
    var hiddenSlickContained = containHiddenLooseSlick();
    if (contained || slickPaused || slickUnslicked || hiddenSlickContained || flickityPaused || state.runs === 1) {
      claim('social-route-mutation-containment:applied', 'RuntimeStabilizer', {
        version: VERSION,
        route: state.route,
        reason: state.lastReason,
        sections: sections.length,
        newlyContained: contained,
        slickPaused: slickPaused,
        slickUnslicked: slickUnslicked,
        hiddenSlickContained: hiddenSlickContained,
        visibleSocialSkipped: state.visibleSocialSkipped,
        nonBleedLooseSlickSkipped: state.nonBleedLooseSlickSkipped,
        protectedSocialCandidates: state.protectedSocialCandidates,
        rollbackRepairs: state.rollbackRepairs,
        flickityPaused: flickityPaused
      });
      event('SOCIAL_ROUTE_MUTATION_CONTAINMENT', {
        version: VERSION,
        route: state.route,
        reason: state.lastReason,
        sections: sections.length,
        slickUnslicked: slickUnslicked,
        hiddenSlickContained: hiddenSlickContained
      });
    }
    claim('social-route-mutation-containment:rollback-guard', 'RuntimeStabilizer', {
      version: VERSION,
      route: state.route,
      protectedSocialCandidates: state.protectedSocialCandidates,
      visibleSocialSkipped: state.visibleSocialSkipped,
      nonBleedLooseSlickSkipped: state.nonBleedLooseSlickSkipped,
      rollbackRepairs: state.rollbackRepairs,
      policy: 'hidden-commerce-only-no-canonical-dsd-surfaces'
    });
    return state;
  }

  var timer = null;
  var observer = null;
  var lateTimers = [];

  function schedule(reason){
    if (!isSocialRoute()) return;
    if (state.scheduled) { state.skippedSchedules++; }
    state.scheduled = true;
    clearTimeout(timer);
    timer = setTimeout(function(){
      state.scheduled = false;
      run(reason);
    }, 220);
  }

  function defer(fn, ms){
    var id = setTimeout(fn, ms);
    lateTimers.push(id);
    return id;
  }

  function cleanup(reason){
    try { clearTimeout(timer); } catch(e) {}
    timer = null;
    state.scheduled = false;
    lateTimers.forEach(function(id){ try { clearTimeout(id); } catch(e) {} });
    lateTimers = [];
    if (observer) {
      try { observer.disconnect(); state.observerDisconnected = true; } catch(e2) {}
      observer = null;
    }
    claim('social-route-mutation-containment:cleanup', 'RuntimeStabilizer', {
      version: VERSION,
      route: state.route,
      reason: reason || 'cleanup',
      runs: state.runs,
      sectionsContained: state.sectionsContained,
      slickUnslicked: state.slickUnslicked,
      hiddenSlickContained: state.hiddenSlickContained,
      visibleSocialSkipped: state.visibleSocialSkipped,
      nonBleedLooseSlickSkipped: state.nonBleedLooseSlickSkipped,
      protectedSocialCandidates: state.protectedSocialCandidates,
      rollbackRepairs: state.rollbackRepairs,
      skippedSchedules: state.skippedSchedules
    });
  }

  function boot(){
    if (!isSocialRoute()) return;
    run('boot');
    defer(function(){ run('post-defer'); }, 900);
    defer(function(){ run('late-app-scan'); }, 2200);
    defer(function(){ run('slick-late-scan'); }, 4200);
    if ('MutationObserver' in window) {
      observer = new MutationObserver(function(records){
        var relevant = false;
        var added = 0;
        for (var i=0;i<records.length;i++) {
          var r = records[i];
          if (!r || r.type !== 'childList') continue;
          var nodes = Array.prototype.slice.call(r.addedNodes || []);
          added += nodes.length;
          if (nodes.some(function(n){
            return n && n.nodeType === 1 && (
              isBleedSection(n) ||
              (n.querySelector && qsa(n, SECTION_SELECTOR).some(isBleedSection))
            );
          })) { relevant = true; break; }
        }
        state.lastObservedAddedNodes = added;
        if (relevant) schedule('childlist-mutation');
      });
      try {
        // Deliberately childList-only: body/header class/style churn is already reported
        // by diagnostics and should not wake this containment system on every animation tick.
        observer.observe(document.body || document.documentElement, { childList:true, subtree:true });
        state.observerReady = true;
      } catch(e) {}
      claim('social-route-mutation-containment:observer-ready', 'RuntimeStabilizer', {
        version: VERSION,
        route: state.route,
        mode: 'childlist-only-hidden-commerce-rollback-guard'
      });
    }
    window.addEventListener('pagehide', function(){ cleanup('pagehide'); }, { once:true });
    window.addEventListener('beforeunload', function(){ cleanup('beforeunload'); }, { once:true });
  }

  window.DSDSocialRouteMutationContainment = {
    version: VERSION,
    state: state,
    run: run,
    cleanup: cleanup,
    snapshot: function(){ return JSON.parse(JSON.stringify(state)); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
